
import Vue, { PropType } from "vue";
import { mapMutations, mapGetters, mapActions } from "vuex";
import * as Sentry from "@sentry/vue";
import LargeSelect from "~/components/form/LargeSelect.vue";
import Box from "~/entities/Box";
import Units from "~/entities/Units";
import NoticeEntity from "~/entities/Notice";

interface IPresetPackage {
  length: any;
  width: any;
  height: any;
  weight: any;
  label: string;
}
interface IUserPresetPackage {
  length: any;
  width: any;
  height: any;
  weight: any;
  label: string;
}
export default Vue.extend({
  components: { LargeSelect },
  model: {
    prop: "box",
    event: "update",
  },
  props: {
    packagePresets: {
      required: false,
      type: [] as PropType<Array<IPresetPackage>>,
      default: () => [],
    },
    packageCategories: {
      required: false,
      type: [] as PropType<Array<any>>,
      default: () => [],
    },
    insuranceOptions: {
      required: false,
      type: [] as PropType<Array<any>>,
      default: () => [],
    },
    currentUnit: {
      required: false,
      type: Object,
      default: () => new Units(),
    },
    box: {
      required: false,
      type: Object,
      default: () => new Box(),
    },
    index: {
      required: false,
      type: Number,
      default: 0,
    },
    arialabel: {
      required: false,
      type: String,
      default: "",
    },
    userPresets: {
      required: false,
      type: [] as PropType<Array<IUserPresetPackage>>,
      default: () => [],
    },
    validationErrors: {
      required: false,
      type: Array,
      default: () => [],
    },
    fieldShading: {
      required: false,
      type: Object,
      // Per-field state: '' (no border) | 'warn' (orange) | 'danger' (red).
      default: () => ({ length: '', width: '', height: '', weight: '' }),
    },
  },
  data() {
    return {
      isOpen: false as boolean,
      innerBox: (this as any).box as any as Box,

      tooltip_MarketValue: null as NoticeEntity | null,
      tooltip_Weight: null as NoticeEntity | null,
      tooltip_BoxCaseDims: null as NoticeEntity | null
    };
  },
  computed: {
    // Vuex getters
    ...mapGetters({
      boxes: "ship/boxes",
    }),
    // Returns a getter so the template can do :class="fieldShadingClassFor('length')".
    fieldShadingClassFor(): (field: 'length' | 'width' | 'height' | 'weight') => string {
      const map: Record<string, string> = {
        warn: 'bf-form-warn-col',
        danger: 'bf-form-danger-col',
      };
      const shading = (this as any).fieldShading || {};
      return (field) => map[shading[field]] || '';
    },
    length: {
      get(): number {
        return (this as any).boxes[(this as any).index]?.length;
      },
      set(value: number) {
        (this as any).setBoxProperty({
          name: "length",
          value: this.cleanDimensionInputs(value),
          index: (this as any).index,
        });
      },
    } as any,
    width: {
      get() {
        return (this as any).boxes[(this as any).index]?.width;
      },
      set(value: number) {
        (this as any).setBoxProperty({
          name: "width",
          value: this.cleanDimensionInputs(value),
          index: (this as any).index,
        });
      },
    } as any,
    height: {
      get() {
        return (this as any).boxes[(this as any).index]?.height;
      },
      set(value: number) {
        (this as any).setBoxProperty({
          name: "height",
          value: this.cleanDimensionInputs(value),
          index: (this as any).index,
        });
      },
    } as any,
    weight: {
      get() {
        return (this as any).boxes[(this as any).index]?.weight;
      },
      set(value: number) {
        (this as any).setBoxProperty({
          name: "weight",
          value: this.cleanDimensionInputs(value),
          index: (this as any).index,
        });
      },
    } as any,
    marketValue: {
      get(): number {
        return (this as any).boxes[(this as any).index]?.marketValue;
      },
      set(value: number) {
        (this as any).setBoxProperty({
          name: "marketValue",
          value,
          index: (this as any).index,
        });
      },
    } as any,
  },
  async mounted() {
    await this.preloadTooltipNotices();
  },
  methods: {
    ...mapMutations({
      setBox: "ship/setBox",
      setBoxProperty: "ship/setBoxProperty",
    }),
    ...mapActions({
      convertUnits: "ship/convertUnits",
      getProtectionOption: "ship/getProtectionOption",
      requestAlert: "errorsBag/requestAlert",
      getAlert: "errorsBag/getAlert",
    }),
    async preloadTooltipNotices(): Promise<void> {
      this.tooltip_MarketValue = await this.getAlert({ name: "ToolTip-MarketValue" });
      this.tooltip_Weight = await this.getAlert({ name: "ToolTip-Weight" });
      this.tooltip_BoxCaseDims = await this.getAlert({ name: "ToolTip-BoxCaseDims" });
    },
    preventNumericInputOnPaste($event: any) {
      const pastedData = $event.clipboardData.getData("text/plain");
      const regex = /^[0-9]+$/; // Regular expression to match only numbers
      const isOnlyNumbers = regex.test(pastedData);
      if (!isOnlyNumbers) {
        $event.preventDefault();
      }
    },
    preventNumericInput($event: any) {
      const keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if (!(keyCode > 47 && keyCode < 58)) {
        $event.preventDefault();
      }
    },
    preventNumericValueInput(this: any) {
      // Use a regular expression to allow numeric characters and a decimal point
      // Use a regular expression to allow numbers with a maximum of one dot and two decimal places
      this.marketValue = this.marketValue?.replace(
        /^(\d+)\.(\d{0,2}).*|[^0-9.].*|^.*(\..*)/g,
        "$1.$2$3"
      );
      this.marketValue = this.marketValue?.includes("..")
        ? ""
        : this.marketValue;

      // limit the maximum length to 8 characters just in case.
      if (this.marketValue?.length > 8) {
        this.marketValue = this.marketValue?.slice(0, 8);
      }
    },
    onShowPresets() {
      (this as any).isOpen = !(this as any).isOpen;
    },
    close() {
      this.isOpen = false;
    },
    selectPresets(preset) {
      (this as any).isOpen = false;
      for (const properties in preset) {
        if (properties === "label") {
          (this as any).setBoxProperty({
            name: "preset",
            value: preset.label,
            index: (this as any).index,
          });
        } else if (
          properties === "description" ||
          properties === "insideDescription"
        ) {
          (this as any).setBoxProperty({
            name: "insideDescription",
            value: preset[String(properties)],
            index: (this as any).index,
          });
        } else {
          (this as any).setBoxProperty({
            name: String(properties),
            value: preset[String(properties)],
            index: (this as any).index,
          });
        }
      }

      if (
        (this as any).currentUnit &&
        (this as any).currentUnit.length &&
        ((this as any).currentUnit.length as string).toLowerCase() !==
          preset.linearUnit.toLowerCase()
      ) {
        (this as any).convertUnits({
          units: ((this as any).currentUnit as Units).length,
          index: (this as any).index,
        });
      }
      (this as any).validateBoxes("");
    },
    async selectUserPresets(preset) {
      try {
        (this as any).isOpen = false;
        for (const properties in preset) {
          if (properties === "label") {
            (this as any).setBoxProperty({
              name: "preset",
              value: preset.label,
              index: (this as any).index,
            });
          } else if (
            properties === "description" ||
            properties === "insideDescription"
          ) {
            (this as any).setBoxProperty({
              name: "insideDescription",
              value: preset[String(properties)],
              index: (this as any).index,
            });
          } else {
            (this as any).setBoxProperty({
              name: String(properties),
              value: preset[String(properties)],
              index: (this as any).index,
            });
          }
        }
        if (
          (this as any).currentUnit &&
          (this as any).currentUnit.length &&
          ((this as any).currentUnit.length as string).toLowerCase() !==
            preset.linearUnit.toLowerCase()
        ) {
          (this as any).convertUnits({
            units: ((this as any).currentUnit as Units).length,
            index: (this as any).index,
          });
        }

        // then, select category -> set changes
        for (let i = 0; i < (this as any).packageCategories.length; i++) {
          if ((this as any).packageCategories[i].code === preset.categoryCode) {
            (this as any).setBoxProperty({
              name: "contents",
              value: (this as any).packageCategories[i],
              index: (this as any).index,
            });
            break;
          }
        }

        // here, get coverage that exactly matches an insuranceOption, otherwise set it as a custom value
        let foundAMatch = false;
        for (let i = 0; i < (this as any).insuranceOptions.length; i++) {
          if (preset.coverage === (this as any).insuranceOptions[i].coverage) {
            (this as any).setBoxProperty({
              name: "premiumProtection",
              value: (this as any).insuranceOptions[i],
              index: (this as any).index,
            });
            foundAMatch = true;
            break;
          }
        }
        if (!foundAMatch) {
          const fee = 0;
          let afterText;
          const coverage = preset.coverage;

          if (coverage < 100) {
            (this as any).setBoxProperty({
              name: "premiumProtection",
              value: (this as any).insuranceOptions[0],
              index: (this as any).index,
            }); 
          } else if (coverage > 20000) {
            const option20K = await (this as any).getProtectionOption(20000);
            if (option20K) {
              (this as any).setBoxProperty({
                name: "premiumProtection",
                value: option20K,
                index: (this as any).index,
              }); 
            }
          } else {
            const option = await (this as any).getProtectionOption(coverage);
            if (option) {
              (this as any).setBoxProperty({
                name: "premiumProtection",
                value: option,
                index: (this as any).index,
              });
            }
          }
        }
        (this as any).validateBoxes("");
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    setPresetToCustom() {
      (this as any).setBoxProperty({
        name: "preset",
        value: "custom",
        index: (this as any).index,
      });
      (this as any).setBoxProperty({
        name: "safeDimensions",
        value: false,
        index: (this as any).index,
      });
    },
    validateBoxes(input: string) {
      (this as any).$emit("validateBoxes", input);
    },
    selectionMade(selection: any) {
      try {
        const name = selection.name;
        if (name === "contents") {
          // this was added on BIK-244, since it was causing errors with restoring searches one after the other - I know, wrong task but it should prevent lots of headaches... or cause more
          (this as any).setBoxProperty({
            name: "contents",
            value: selection.option,
            index: (this as any).index,
          });
          // (this as any).innerBox.contents = selection.option;
        } else if (name === "premiumProtection") {
          // this was added on BIK-244, since it was causing errors with restoring searches one after the other - I know, wrong task but it should prevent lots of headaches... or cause more
          (this as any).setBoxProperty({
            name: "premiumProtection",
            value: selection.option,
            index: (this as any).index,
          });
          // (this as any).innerBox.premiumProtection = selection.option;
        }
        (this as any).$emit("validateBoxes", name);
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    convertDimensions() {
      const units = (this as any).currentUnit as Units;
      (this as any).convertUnits({
        units: units.length,
        index: (this as any).index,
      });
    },
    cleanDimensionInputs(value) {
      if (value > 0 && value < 0.01) {
        value = 0;
      } else if (value > 0) {
        const decimalRegex = /^-?\d+(\.\d*)?$/;
        if (decimalRegex.test(value)) {
          let auxValue = value;
          if (typeof value !== "number") {
            auxValue = parseFloat(value);
          }
          if (isNaN(auxValue) || !isFinite(auxValue)) {
            return auxValue;
          }
          const numberString = value.toString();
          const parts = numberString.split(".");
          if (parts.length === 2) {
            parts[0] = parts[0]?.replace(/^0+/, "");
            if (parts[0] === "") {
              parts[0] = "0";
            }
          } else {
            value = auxValue;
          }
        } else {
          value = Number(value.toString());
        }
      }
      return value;
    },
  },
  watch: {
    currentUnit(newVal, oldVal) {
      if (newVal?.length && oldVal?.length && newVal.length !== oldVal.length) {
        (this as any).convertDimensions();
      }
    },
  },
});
